import { Button } from '@1trading/react/components/ui/button';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { PopoverContent, PopoverControlled, PopoverTrigger } from '@1trading/react/components/ui/popover';
import { PseudoInputButton, PseudoInputIcon, PseudoInputLabel, PseudoInputOptions } from '@1trading/react/components/ui/pseudo-input';
import { ChevronsUpDown, Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import PersonalInformationFormFields from '@/features/onboarding/personal-information/components/fields';
import { type PersonalInformation, type PersonalInformationTaxResidency, TAX_RESIDENCES_MAX } from '@/features/onboarding/personal-information/schema';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { taxIdentificationNumberFormatsQueryOptions, useTaxIdentificationNumberFormats } from '@/hooks/queries/use-tax-identification-number-formats';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';
import { queryClient } from '@/lib/query-client';
import { getPreviousTaxIdForCountry, reportTaxIdentificationNumberFormatError } from '@/utils/tax-identification-number';

export const PersonalInformationFormFieldsetTaxResidencyLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('personalInformation.taxResidencyTitle')}</PageHeadingTitle>
        <PageHeadingDescription>{t('personalInformation.taxResidencyDescription')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
PersonalInformationFormFieldsetTaxResidencyLegend.displayName = 'PersonalInformationFormFieldsetTaxResidencyLegend';

const PersonalInformationFormFieldsetTaxResidencyTaxIdentificationNumber = ({ index }: { index: number }) => {
  const form = useFormContext<PersonalInformationTaxResidency>();
  const { countriesAsOptions } = useMainstreamCountries();
  const { data: formats } = useTaxIdentificationNumberFormats();
  const { t } = useTranslation();
  const country = form.watch(`taxResidences.${index}.country`);
  const countryName = countriesAsOptions.find((option) => option.value === country)?.label;
  const coverage = formats?.coverage[country];
  const example = formats?.examples[country];

  if (!country) return null;

  if (coverage?.status === 'no-tin-issued') {
    return <p className="col-span-2 text-sm text-muted-foreground">{t('personalInformation.taxIdentificationNumberNotIssued', { country: countryName })}</p>;
  }

  return (
    <FormField
      control={form.control}
      name={`taxResidences.${index}.taxId`}
      render={({ field }) => (
        <FormItem className="col-span-2">
          <FormLabel>{t('personalInformation.taxIdentificationNumber')}*</FormLabel>
          <FormControl>
            <Input
              {...field}
              placeholder={
                example
                  ? t('nationalClientIdentifier.withExample', { text: t('personalInformation.placeholders.taxIdentificationNumber'), example })
                  : t('personalInformation.placeholders.taxIdentificationNumber')
              }
            />
          </FormControl>
          {coverage?.name ? (
            <FormDescription>
              {t('personalInformation.taxIdentificationNumberHint', {
                country: countryName,
                name: coverage.abbreviation ? `${coverage.name} (${coverage.abbreviation})` : coverage.name,
              })}
            </FormDescription>
          ) : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
PersonalInformationFormFieldsetTaxResidencyTaxIdentificationNumber.displayName = 'PersonalInformationFormFieldsetTaxResidencyTaxIdentificationNumber';

export const PersonalInformationFormFieldsetTaxResidency = () => {
  const form = useFormContext<PersonalInformationTaxResidency & Pick<PersonalInformation, 'taxId'>>();
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'taxResidences' });
  const { countriesAsOptions } = useMainstreamCountries();
  const { data: formats } = useTaxIdentificationNumberFormats();
  const { t } = useTranslation();
  const taxResidences = form.watch('taxResidences');

  const otherTaxResidences = (index: number) => taxResidences.filter((_, otherIndex) => otherIndex !== index);

  const countryOptionsFor = (index: number) => {
    const otherCountries = otherTaxResidences(index).map(({ country }) => country);

    return countriesAsOptions.filter((option) => !otherCountries.includes(option.value));
  };

  const previousTaxIdFor = (country: string, index: number) => {
    if (!formats) return '';

    const otherTaxIds = otherTaxResidences(index).map(({ taxId }) => taxId);

    return getPreviousTaxIdForCountry({ country, otherTaxIds, previousTaxId: form.getValues('taxId'), validate: formats.validate });
  };

  return (
    <div className="space-y-6">
      {fields.map((taxResidence, index) => (
        <PersonalInformationFormFields key={taxResidence.id}>
          <FormField
            control={form.control}
            name={`taxResidences.${index}.country`}
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('personalInformation.taxResidenceCountry')}*</FormLabel>
                <PopoverControlled>
                  {(_, setOpen) => (
                    <>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                            <PseudoInputLabel>
                              {field.value ? countriesAsOptions.find((option) => option.value === field.value)?.label : t('personalInformation.placeholders.taxResidenceCountry')}
                            </PseudoInputLabel>
                            <PseudoInputIcon>
                              <ChevronsUpDown />
                            </PseudoInputIcon>
                          </PseudoInputButton>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="p-0" stretch>
                        <PseudoInputOptions
                          empty={t('statuses.noResults')}
                          options={countryOptionsFor(index)}
                          placeholder={t('fundamentals.searchCountries')}
                          value={field.value}
                          onValueChange={(value) => {
                            setOpen(false);
                            // The options report a click on the selected country too, which must not replace a typed TIN
                            if (value === field.value) return;

                            field.onChange(value);
                            form.setValue(`taxResidences.${index}.taxId`, previousTaxIdFor(value, index));
                            form.clearErrors(`taxResidences.${index}.taxId`);
                          }}
                        />
                      </PopoverContent>
                    </>
                  )}
                </PopoverControlled>
                <FormMessage />
              </FormItem>
            )}
          />
          <PersonalInformationFormFieldsetTaxResidencyTaxIdentificationNumber index={index} />
          {fields.length > 1 ? (
            <Button className="col-span-2 justify-self-end gap-2 text-destructive hover:text-destructive" size="sm" type="button" variant="ghost" onClick={() => remove(index)}>
              <Trash2 className="size-4" />
              {t('personalInformation.removeTaxResidence')}
            </Button>
          ) : null}
        </PersonalInformationFormFields>
      ))}
      {fields.length < TAX_RESIDENCES_MAX ? (
        <Button className="gap-2" size="sm" type="button" variant="outline" onClick={() => append({ country: '', taxId: '' })}>
          <Plus className="size-4" />
          {t('personalInformation.addTaxResidence')}
        </Button>
      ) : null}
      <p className="text-sm">{t('personalInformation.taxResidencyDeclaration')}</p>
    </div>
  );
};
PersonalInformationFormFieldsetTaxResidency.displayName = 'PersonalInformationFormFieldsetTaxResidency';

// The stepper's own validation has no failure callback, so a rejected TIN is reported as the step is left
const beforeStepLeave: FormStepGuard<PersonalInformation> = async ({ direction, form }, next) => {
  if (direction === 'next') {
    const { validate } = await queryClient.ensureQueryData(taxIdentificationNumberFormatsQueryOptions);

    for (const { country, taxId } of form.getValues('taxResidences')) {
      reportTaxIdentificationNumberFormatError({ country, taxId, validate });
    }
  }

  return next();
};

PersonalInformationFormFieldsetTaxResidency.beforeStepLeave = beforeStepLeave;
